import { Map } from 'immutable'
import get from 'lodash/get'
import { setInboundTrackingAffiliateData } from '../inbound-tracking/actions'
import { ERROR_EMAIL_INVALID } from '../email-signup/index'
import { addToasty } from '../toasty/actions'
import { RESOLVER_TYPE_SHARE } from '../resolver/types'
import { SET_RESOLVER_DATA } from '../resolver/actions'
import { SET_APP_BOOTSTRAP_PHASE } from '../app/actions'
import { setMediaLang, resetMedia } from '../media/actions'
import { get as getLocalInboundTrackingData } from '../inbound-tracking/index'
import * as api from '.'
import {
  CREATE_SHARE,
  CREATED_SHARE,
  GET_USER_SHARES,
  SET_SHARE_DATA,
  SET_USER_SHARES,
  SET_SHARE_SOURCE,
  SET_ACCOUNT_SHARES,
  GET_ACCOUNT_SHARES,
  GET_USER_HAS_SHARED,
  SET_USER_HAS_SHARED,
  SET_SHARE_CONVERSION,
  SET_SHARE_QUALIFIED_VIEW,
  SET_SHARE_PERSONALIZATION,
  DUPLICATE_SHARE_EMAIL_CAPTURE,
  DUPLICATED_SHARE_EMAIL_CAPTURE,
  SET_SHARE_REMOVE_PERSONALIZATION,
} from './actions'

function isSharePage ({ state }) {
  const { resolver } = state
  return resolver.getIn(['data', 'type'])
    === RESOLVER_TYPE_SHARE
}

export function createShare ({ takeFirst }) {
  return takeFirst(
    CREATE_SHARE,
    async ({ action, state }) => {
      const { auth } = state
      const { payload: inputs } = action
      const {
        personalization, source, type, contentId,
      } = inputs
      const payload = await api.createShare({
        personalization,
        contentId,
        source,
        auth,
        type,
      })
      return {
        type: CREATED_SHARE,
        payload,
      }
    },
  )
}

export function duplicateShareEmailCapture ({ takeFirst }) {
  return takeFirst(
    DUPLICATE_SHARE_EMAIL_CAPTURE,
    async ({ action, state }) => {
      const { user, inboundTracking } = state
      const language = user.getIn(['data', 'language', 0], 'en')
      const { payload: inputs } = action
      const { token, email } = inputs
      const data = await api.duplicateShareEmailCapture({
        token,
        email,
        language,
        inboundTracking,
      })
      const payload = { data }
      return {
        type: DUPLICATED_SHARE_EMAIL_CAPTURE,
        payload,
      }
    },
  )
}

// -----------------------------------
// Watcher for toasty when report is made
// -----------------------------------
export function shareEmailCaptureCreateToasty ({ after }) {
  return after([
    DUPLICATED_SHARE_EMAIL_CAPTURE,
  ], async ({ dispatch, state, action }) => {
    const { staticText } = state
    const { payload } = action
    const { data: payloadData } = payload
    const data = payloadData.get('data', Map())
    const success = data.get('success', false)
    const errorCode = data.get('errorCode', undefined)
    const successMsg = staticText.getIn(['data', 'sharePage', 'data', 'successExpiredMessage'])
    const errorMsg = (errorCode && errorCode === ERROR_EMAIL_INVALID)
      ? staticText.getIn(['data', 'sharePage', 'data', 'errorMessageInvalidEmail'])
      : staticText.getIn(['data', 'sharePage', 'data', 'errorMessageRegistrationError'])
    const msg = success ? successMsg : errorMsg
    dispatch(addToasty(msg))
  })
}

export function getHasShared ({ takeMaybe }) {
  return takeMaybe(
    GET_USER_HAS_SHARED,
    ({ state }) => {
      const { auth, share } = state

      if (share.get('hasShared')) {
        return null
      }

      return async function getHasSharedEffect () {
        const payload = await api.getHasShared({ auth })
        return {
          type: SET_USER_HAS_SHARED,
          payload,
        }
      }
    },
  )
}

export function getUserShares ({ takeEvery }) {
  return takeEvery(
    GET_USER_SHARES,
    async ({ state }) => {
      const { auth } = state
      const payload = await api.getUserShares({ auth })
      return {
        type: SET_USER_SHARES,
        payload,
      }
    },
  )
}

export function getAccountShares ({ takeEvery }) {
  return takeEvery(
    GET_ACCOUNT_SHARES,
    async ({ state }) => {
      const { auth } = state
      const payload = await api.getAccountShares({ auth })
      return {
        type: SET_ACCOUNT_SHARES,
        payload,
      }
    },
  )
}

export function setQualifiedView ({ after }) {
  return after(
    SET_SHARE_QUALIFIED_VIEW,
    async ({ action }) => {
      const token = get(action, ['payload', 'token'])
      await api.setQualifiedView({ token })
    },
  )
}

export function addSource ({ after }) {
  return after(
    SET_SHARE_SOURCE,
    async ({ state, action }) => {
      const { auth } = state
      const token = get(action, ['payload', 'token'])
      const source = get(action, ['payload', 'source'])
      await api.setShareSource({ auth, token, source })
    },
  )
}

export function setConversion ({ after }) {
  return after(
    SET_SHARE_CONVERSION,
    async ({ action }) => {
      const token = get(action, ['payload', 'token'])
      await api.setConversion({ token })
    },
  )
}

export function addPersonalization ({ takeLatest }) {
  return takeLatest(
    SET_SHARE_PERSONALIZATION,
    async ({ state, action }) => {
      const { auth } = state
      const { payload } = action
      const { personalization, token } = payload
      await api.addPersonalization({ auth, token, personalization })
    },
  )
}

export function removePersonalization ({ takeLatest }) {
  return takeLatest(
    SET_SHARE_REMOVE_PERSONALIZATION,
    async ({ action, state }) => {
      const { auth } = state
      const { payload } = action
      const { token } = payload
      await api.removePersonalization({ auth, token })
    },
  )
}

// -----------------------------------
// Watcher when share data is ready.
// -----------------------------------
export function watchSetShare ({ after }) {
  return after(
    [
      SET_SHARE_DATA,
      SET_APP_BOOTSTRAP_PHASE,
    ],
    async ({ dispatch, state }) => {
      const {
        app,
        auth,
        share,
        media,
        user,
      } = state
      if (app.get('bootstrapComplete')) {
        const existingMediaLanguage = media.getIn(['data', 'mediaLang'])
        const shareLanguage = share.getIn(['data', 'fullMedia', 'mediaUrls', 'language'])
        const rfd = share.getIn(['data', 'userReferralId'])

        if (rfd) {
          const uid = user.getIn(['data', 'uid'], -1) || -1
          const localInboundTrackingData = await getLocalInboundTrackingData({ uid }) || Map()
          const localRfd = localInboundTrackingData.get('rfd')

          if (rfd !== localRfd) {
            dispatch(setInboundTrackingAffiliateData({
              rfd,
              utmSource: 'email',
              source: 'SHARE',
              auth,
            }))
          }
        }

        // Set the mediaLanguage from share store if has not been populated.
        if (shareLanguage && !existingMediaLanguage) {
          dispatch(setMediaLang(shareLanguage))
        }
      }
    },
  )
    .when(isSharePage)
}

// -----------------------------------
// Watcher when share page is unmounted.
// -----------------------------------
export function watchSharePageUnmount ({ before }) {
  return before(SET_RESOLVER_DATA, async ({ action, dispatch }) => {
    const next = get(action, 'payload.data.path')

    // Reset the media if leaving the page.
    if (next !== RESOLVER_TYPE_SHARE) {
      dispatch(resetMedia())
    }
  }).when(isSharePage)
}
